import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Cog, Wrench, AlertTriangle, Package, Clock, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentWorks, setRecentWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, worksRes] = await Promise.all([
        axios.get(`${API}/stats/client`),
        axios.get(`${API}/works`)
      ]);
      setStats(statsRes.data);
      setRecentWorks(worksRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Mis Máquinas', value: stats?.machines_count || 0, icon: Cog, color: 'sky', link: '/client/machines' },
    { label: 'Trabajos Realizados', value: stats?.works_count || 0, icon: Wrench, color: 'emerald', link: '/client/works' },
    { label: 'Incidencias Pendientes', value: stats?.incidents_pending || 0, icon: AlertTriangle, color: 'amber', link: '/client/incidents' },
    { label: 'Esperando Repuesto', value: stats?.machines_pending_parts || 0, icon: Package, color: 'rose', link: '/client/machines' },
  ];

  const getTypeBadge = (type) => {
    const colors = {
      preventivo: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      correctivo: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      averia: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    };
    const labels = { preventivo: 'Preventivo', correctivo: 'Correctivo', averia: 'Avería' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-mono uppercase tracking-wider border ${colors[type]}`}>
        {labels[type]}
      </span>
    );
  };

  return (
    <Layout title={`Bienvenido, ${user?.name}`}>
      <div className="space-y-6 animate-fade-in" data-testid="client-dashboard">
        {/* Company Info */}
        <Card className="bg-gradient-to-r from-sky-500/10 to-transparent border-sky-500/20">
          <CardContent className="p-6">
            <h3 className="font-rajdhani text-2xl font-bold text-white mb-1">{user?.company_name}</h3>
            <p className="text-slate-400">Panel de control de mantenimiento</p>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link key={stat.label} to={stat.link}>
                <Card className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all cursor-pointer group h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                          {stat.label}
                        </p>
                        <p className="text-4xl font-rajdhani font-bold text-white">
                          {loading ? '-' : stat.value}
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-sm bg-${stat.color}-500/10 flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 text-${stat.color}-400`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Recent Works */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="border-b border-slate-800">
            <CardTitle className="font-rajdhani text-xl font-semibold text-white flex items-center gap-2">
              <Wrench className="w-5 h-5 text-emerald-400" />
              Últimos Trabajos Realizados
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Cargando...</div>
            ) : recentWorks.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No hay trabajos registrados</div>
            ) : (
              <div className="divide-y divide-slate-800">
                {recentWorks.map((work) => (
                  <div key={work.id} className="p-4 hover:bg-slate-800/30 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getTypeBadge(work.work_type)}
                          {work.machine_name && (
                            <>
                              <span className="text-slate-600">•</span>
                              <span className="text-sm text-sky-400">{work.machine_name}</span>
                            </>
                          )}
                        </div>
                        <p className="text-white truncate">{work.description}</p>
                        <p className="text-sm text-slate-500 mt-1">Técnico: {work.technician}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-sm text-slate-400 flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(work.date).toLocaleDateString('es-ES')}
                        </span>
                        <span className="text-xs text-slate-500 font-mono flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {work.start_time} - {work.end_time}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
